import React, { useState } from 'react';
import type { StoryFailureAnalysis } from '../../../../../../shared/types/epics';
import { StatusBadge } from '../../../../components/ui';
import { formatDate } from '../format';

// FORGE-207: per-story "why it stopped". Surfaces data Forge already writes on
// a force-complete / failure but never showed: the real exit reason
// (no_progress vs max_iterations), the agent's last unwritten diagnosis, the
// stall reason, and the retry-analyst brief (verdict + fix steps). Collapsible
// so it doesn't clutter healthy stories.
export const FailureAnalysisPanel: React.FC<{ analysis: StoryFailureAnalysis }> = ({ analysis }) => {
  const [open, setOpen] = useState(false);
  const a = analysis;
  const list = (label: string, items: string[]) =>
    items.length > 0 && (
      <div className="mt-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
        <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-xs text-slate-700">
          {items.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      </div>
    );

  return (
    <div className="mx-4 mt-3 rounded-md border border-rose-200 bg-rose-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <span className="text-sm font-semibold text-rose-900">Why it stopped</span>
        {a.exitReason && (
          <StatusBadge tone="danger">{a.exitReason}</StatusBadge>
        )}
        {a.retryAdvice?.verdict && (
          <StatusBadge tone={a.retryAdvice.verdict === 'futile' ? 'danger' : 'warning'}>
            retry-analyst: {a.retryAdvice.verdict}
          </StatusBadge>
        )}
        <span className="ml-auto text-xs text-slate-500">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="border-t border-rose-200 px-3 py-2.5">
          {a.stallReason && (
            <div className="text-xs text-slate-700">
              <span className="font-semibold">Stall reason:</span> {a.stallReason}
            </div>
          )}
          {a.lastAttemptNotes && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Agent's last diagnosis
              </div>
              <div className="mt-0.5 whitespace-pre-wrap font-mono text-xs text-slate-700">
                {a.lastAttemptNotes}
              </div>
            </div>
          )}
          {a.retryAdvice?.diagnosis && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Retry-analyst diagnosis
                {a.retryAdvice.rootCauseKind ? ` (${a.retryAdvice.rootCauseKind})` : ''}
              </div>
              <div className="mt-0.5 whitespace-pre-wrap text-xs text-slate-700">
                {a.retryAdvice.diagnosis}
              </div>
            </div>
          )}
          {a.retryAdvice && list('Fix steps', a.retryAdvice.fixSteps)}
          {a.retryAdvice && list("Don't repeat", a.retryAdvice.dontRepeat)}
          {a.retryAdvice && list('What worked', a.retryAdvice.keep)}
          {a.lastAttemptAt && (
            <div className="mt-2 text-[11px] text-slate-400">
              last attempt {formatDate(a.lastAttemptAt)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
